/**
 * Module #34 Phase A — /admin/copilot full-page surface.
 *
 * Chunk 2 layout: left rail = conversation history sidebar, right = chat.
 * useCopilot drives the active chat; useCopilotConversations drives the
 * sidebar list. Clicking a sidebar row loads that conversation into the
 * chat; "New chat" resets the chat to a fresh conversation_id.
 */

import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CopilotChat } from '../../components/copilot/CopilotChat';
import { ConversationSidebar } from '../../components/copilot/ConversationSidebar';
import { useCopilot } from '../../hooks/useCopilot';
import { useCopilotConversations } from '../../hooks/useCopilotConversations';

export function AdminCopilot() {
  const copilot = useCopilot();
  const conversations = useCopilotConversations();
  const { refresh } = conversations;

  // When a brand-new conversation first receives its server id (i.e. the
  // first turn just completed), silently pull the list so the new chat
  // appears in the sidebar with its auto-generated title — no "Loading…"
  // flash, no full reload.
  const prevConversationId = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevConversationId.current;
    prevConversationId.current = copilot.conversationId;
    if (prev === null && copilot.conversationId !== null) {
      void refresh({ silent: true });
    }
  }, [copilot.conversationId, refresh]);

  const onSelect = useCallback(
    (id: string): void => {
      void copilot.loadConversation(id);
    },
    [copilot],
  );

  const onNewChat = useCallback((): void => {
    copilot.reset();
  }, [copilot]);

  return (
    <div
      style={{
        height: 'calc(100vh - 4rem)',
        maxHeight: '52rem',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.75rem)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--admin-hairline, var(--border))',
          background: 'var(--surface-elev)',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          Tenant Copilot
        </h1>
        <div
          style={{
            marginTop: '0.25rem',
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
          }}
        >
          AI assistant for auction operations. 11 read + 5 draft + 15 action tools + a persistent
          memory layer live; vision tools land in an upcoming release.
        </div>
        <Link
          to="/admin/copilot/memory"
          style={{
            display: 'inline-block',
            marginTop: '0.5rem',
            fontSize: '0.85rem',
            color: 'var(--brand-primary, #2264dc)',
          }}
        >
          Manage memory →
        </Link>
      </header>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <ConversationSidebar
          list={conversations}
          activeConversationId={copilot.conversationId}
          onSelect={onSelect}
          onNewChat={onNewChat}
        />
        <CopilotChat copilot={copilot} compact={false} />
      </div>
    </div>
  );
}
